'use client'
import { useEffect, useState } from 'react'
import { Calendar, MapPin, Clock } from 'lucide-react'
import EventCardActions from '@/components/public/EventCardActions'

export function HomeEvents() {
  const [upcoming, setUpcoming] = useState<any[]>([])
  const [past, setPast] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch('/api/events/home')
      .then(res => res.json())
      .then(data => {
        setUpcoming(data.upcoming || [])
        setPast(data.past || [])
        setLoading(false)
      })
      .catch(err => {
        console.error(err)
        setLoading(false)
      })
  }, [])

  if (loading) {
    return (
      <div className="py-16 text-center text-white" style={{ background: '#1a0d04' }}>
        Loading events...
      </div>
    )
  }

  const renderEventCard = (event: any, isPast: boolean) => (
    <div key={event.id} 
      className="rounded-2xl overflow-hidden flex flex-col group transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
      style={{
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(243,156,18,0.2)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
      }}
    >
      {event.banner_url ? (
        <div className="h-48 overflow-hidden relative border-b border-[rgba(243,156,18,0.2)]">
          <img src={event.banner_url} alt={event.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          {event.category && (
            <span className="absolute bottom-3 left-4 px-3 py-1 rounded-full text-[10px] font-bold text-white uppercase tracking-wider shadow-lg"
              style={{ background: 'linear-gradient(90deg, #F39C12, #E67E22)' }}>
              {event.category}
            </span>
          )}
        </div>
      ) : (
        <div className="h-32 flex items-center justify-center relative overflow-hidden border-b border-[rgba(243,156,18,0.2)]" 
             style={{ background: 'linear-gradient(135deg, rgba(243,156,18,0.1), rgba(230,126,34,0.05))' }}>
          <Calendar className="h-12 w-12 text-[#F39C12]/20" />
          {event.category && (
            <span className="absolute bottom-3 left-4 px-3 py-1 rounded-full text-[10px] font-bold text-white uppercase tracking-wider shadow-lg"
              style={{ background: 'linear-gradient(90deg, #F39C12, #E67E22)' }}>
              {event.category}
            </span>
          )}
        </div>
      )}

      <div className="p-6 flex-1 flex flex-col">
        <h3 className="text-xl font-bold text-white mb-2 leading-tight">{event.name}</h3>
        {event.description && (
          <p className="text-sm text-white/70 mb-4 line-clamp-2 flex-1">{event.description}</p>
        )}

        <div className="space-y-2 mb-4 mt-auto">
          {event.start_date && (
            <div className="flex items-center gap-2 text-xs text-white/80 font-medium">
              <Calendar className="h-3.5 w-3.5 text-[#F39C12]" />
              {new Date(event.start_date).toLocaleDateString()}
            </div>
          )}
          {event.event_time && (
            <div className="flex items-center gap-2 text-xs text-white/80 font-medium">
              <Clock className="h-3.5 w-3.5 text-[#F39C12]" />
              {event.event_time}
            </div>
          )}
          {event.location && (
            <div className="flex items-center gap-2 text-xs text-white/80 font-medium">
              <MapPin className="h-3.5 w-3.5 text-[#F39C12]" />
              <span className="truncate">{event.location}</span>
            </div>
          )}
        </div>

        <div className="border-t border-[rgba(255,255,255,0.1)] pt-4 mt-2">
          <EventCardActions 
            slug={event.slug} 
            qrCodePng={event.qrCode?.png_data} 
            isPast={isPast}
            photos={event.photos}
            links={event.links}
          />
        </div>
      </div>
    </div>
  )

  return (
    <section className="py-16 px-4 anim-fade-in-up" style={{ background: '#1a0d04' }}>
      <div className="max-w-6xl mx-auto">
        
        {upcoming.length > 0 && (
          <div className="mb-16">
            <div className="text-center mb-10">
              <h2 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: '2.2rem', color: '#ffffff', marginBottom: '8px' }}>
                Upcoming Events
              </h2>
              <div style={{ width: '60px', height: '3px', background: 'linear-gradient(90deg, #F39C12, #E67E22)', borderRadius: '100px', margin: '16px auto 0' }} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {upcoming.map(e => renderEventCard(e, false))}
            </div>
          </div>
        )}

        {past.length > 0 && (
          <div>
            <div className="text-center mb-10">
              <h2 style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: '2.2rem', color: '#ffffff', marginBottom: '8px' }}>
                Past Events
              </h2>
              <div style={{ width: '60px', height: '3px', background: 'linear-gradient(90deg, #F39C12, #E67E22)', borderRadius: '100px', margin: '16px auto 0' }} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {past.map(e => renderEventCard(e, true))}
            </div>
          </div>
        )}

        {upcoming.length === 0 && past.length === 0 && (
          <div className="text-center text-white/60">
            No events available right now.
          </div>
        )}
      </div>
    </section>
  )
}
